import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Text, SimpleGrid, Link } from '@chakra-ui/react';
import SEO from '../components/SEO';

const TOOLS = [
  { id: '35mm',  label: '35mm',  film: '35mm', frames: 40, aspectW: 3,  aspectH: 2, href: '/tools/35mm.html'  },
  { id: '6x45',  label: '6×4.5', film: '120',  frames: 16, aspectW: 4,  aspectH: 3, href: '/tools/6x45.html'  },
  { id: '6x6',   label: '6×6',   film: '120',  frames: 12, aspectW: 1,  aspectH: 1, href: '/tools/6x6.html'   },
  { id: '6x7',   label: '6×7',   film: '120',  frames: 10, aspectW: 6,  aspectH: 7, href: '/tools/6x7.html'   },
  { id: '6x9',   label: '6×9',   film: '120',  frames: 8,  aspectW: 2,  aspectH: 3, href: '/tools/6x9.html'   },
];

function FramePreviewSVG({ aspectW, aspectH }: { aspectW: number; aspectH: number }) {
  const AMBER = '#c8a01e';
  const containerW = 80;
  const containerH = 56;
  const padding = 8;

  // Scale the frame to fit inside container maintaining aspect ratio
  const maxW = containerW - padding * 2;
  const maxH = containerH - padding * 2;
  const scale = Math.min(maxW / aspectW, maxH / aspectH);
  const fw = aspectW * scale;
  const fh = aspectH * scale;
  const fx = (containerW - fw) / 2;
  const fy = (containerH - fh) / 2;

  return (
    <svg width={containerW} height={containerH} viewBox={`0 0 ${containerW} ${containerH}`}>
      <rect
        x={fx} y={fy}
        width={fw} height={fh}
        fill="none"
        stroke={AMBER}
        strokeWidth="1.5"
        opacity="0.7"
      />
      {/* Corner ticks */}
      <line x1={fx} y1={fy} x2={fx + 4} y2={fy} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx} y1={fy} x2={fx} y2={fy + 4} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx + fw} y1={fy} x2={fx + fw - 4} y2={fy} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx + fw} y1={fy} x2={fx + fw} y2={fy + 4} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx} y1={fy + fh} x2={fx + 4} y2={fy + fh} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx} y1={fy + fh} x2={fx} y2={fy + fh - 4} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx + fw} y1={fy + fh} x2={fx + fw - 4} y2={fy + fh} stroke={AMBER} strokeWidth="1" opacity="0.5" />
      <line x1={fx + fw} y1={fy + fh} x2={fx + fw} y2={fy + fh - 4} stroke={AMBER} strokeWidth="1" opacity="0.5" />
    </svg>
  );
}

const ToolsPage = () => {
  const { t } = useTranslation('tools');
  const { lng } = useParams<{ lng: string }>();
  const currentLanguage = lng || 'en';

  const buildToolHref = (href: string) =>
    `${href}?lang=${currentLanguage}`;

  return (
    <>
      <SEO
        title={currentLanguage === 'es'
          ? 'Generadores de Hojas de Contacto Gratis — Al Grano Con Pablo'
          : 'Free Contact Sheet Generators — Al Grano Con Pablo'}
        description={currentLanguage === 'es'
          ? 'Hojas de contacto digitales gratuitas para 35mm y formato medio. Carga tus escaneos, personaliza e imprime.'
          : 'Free digital contact sheet generators for 35mm and medium format film. Load scans, customise, print or download.'}
        keywords="contact sheet generator, contact sheet 35mm, medium format contact sheet, film photography tools"
      />

      {/* ── Hero ── */}
      <Box bg="white" px={{ base: 6, md: 8 }} pt={{ base: 8, md: 12 }} pb={{ base: 8, md: 10 }}>
        <Box
          maxW="1200px"
          mx="auto"
          textAlign="center"
          py={12}
          border="4px solid black"
        >
          <Text
            fontSize={{ base: '4xl', md: '7xl' }}
            fontWeight="bold"
            fontFamily="Abel"
            color="black"
            lineHeight="1.1"
            mb={6}
          >
            {t('pageTitle').toUpperCase()}
          </Text>

          <Box w="60px" h="2px" bg="black" mx="auto" mb={6} />

          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            fontFamily="Source Code Pro"
            color="black"
            maxW="560px"
            mx="auto"
            lineHeight="1.7"
          >
            {t('pageSubtitle')}
          </Text>
        </Box>
      </Box>

      {/* ── Tool Cards ── */}
      <Box bg="#0a0a0a" px={{ base: 6, md: 8 }} py={{ base: 10, md: 14 }}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} maxW="1200px" mx="auto">
          {TOOLS.map(tool => (
            <Link
              key={tool.id}
              href={buildToolHref(tool.href)}
              _hover={{ textDecoration: 'none' }}
            >
              <Box
                border="1px solid"
                borderColor="#1e1e1e"
                bg="#0f0f0f"
                p={6}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: '#c8a01e' }}
                h="100%"
                display="flex"
                flexDirection="column"
              >
                {/* Frame preview */}
                <Box mb={4} opacity={0.8}>
                  <FramePreviewSVG aspectW={tool.aspectW} aspectH={tool.aspectH} />
                </Box>

                {/* Format label */}
                <Text
                  fontSize="3xl"
                  fontWeight="bold"
                  fontFamily="Abel"
                  color="#c8a01e"
                  lineHeight="1"
                  mb={2}
                >
                  {tool.label}
                </Text>

                {/* Film + frames metadata */}
                <Text
                  fontSize="9px"
                  fontFamily="Source Code Pro"
                  color="#555"
                  letterSpacing="2px"
                  textTransform="uppercase"
                  mb={3}
                >
                  {tool.film} · {tool.frames} {t('framesLabel')}
                </Text>

                {/* Description */}
                <Text
                  fontSize="xs"
                  fontFamily="Source Code Pro"
                  color="#444"
                  lineHeight="1.6"
                  flex="1"
                  mb={5}
                >
                  {t(`formats.${tool.id}.desc`)}
                </Text>

                {/* Open button */}
                <Text
                  fontSize="xs"
                  fontFamily="Source Code Pro"
                  color="#555"
                  letterSpacing="1px"
                  _groupHover={{ color: '#c8a01e' }}
                  transition="color 0.2s"
                  mt="auto"
                >
                  {t('openGenerator')}
                </Text>
              </Box>
            </Link>
          ))}
        </SimpleGrid>
      </Box>

      {/* ── SEO text ── */}
      <Box bg="white" px={{ base: 6, md: 8 }} py={{ base: 12, md: 16 }}>
        <Box maxW="680px" mx="auto">
          <Text
            fontSize={{ base: 'xl', md: '2xl' }}
            fontWeight="bold"
            fontFamily="Abel"
            color="black"
            mb={4}
          >
            {t('seoHeading')}
          </Text>
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            fontFamily="Source Code Pro"
            color="black"
            lineHeight="1.8"
          >
            {t('seoBody')}
          </Text>
        </Box>
      </Box>
    </>
  );
};

export default ToolsPage;
